'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { type ControlBarLoginAction, ControlBarLikeAction, ControlBarMoreAction, ControlBarReportAction, ControlBarSaveAction } from '@/app/[locale]/(worlds)/[roomId]/components/ControlBarActions';
import ControlBarActionTooltip from '@/app/[locale]/(worlds)/[roomId]/components/ControlBarActionTooltip';
import type { ControlBarViewportLayoutProps } from '@/app/[locale]/(worlds)/[roomId]/components/ControlBarViewportLayout';
import FullscreenButton from '@/app/[locale]/(worlds)/[roomId]/components/FullscreenButton';
import SharePopoverButton from '@/app/[locale]/(worlds)/[roomId]/components/SharePopoverButton';
import IconHide from '@/components/assets/icon_control_bar_hide.svg';
import IconViverseLogo from '@/components/assets/icon_control_bar_viverse.svg';
import ViverseButton from '@/components/ViverseButton';
import { type DesktopControlBarSurface, type DesktopControlBarVariant } from '@/constants/control-bar';
import { useAuthContext } from '@/context/AuthContext';
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext';
import type { RecommendRoom } from '@/services/cms';
export type ControlBarDesktopProps = {
  moreRecommendationsPromise?: Promise<RecommendRoom[]> | null;
  onHideAction?: () => void;
  popupContainer?: HTMLDivElement | null;
  surface?: DesktopControlBarSurface;
  variant?: DesktopControlBarVariant;
};
type ControlBarDesktopViewportOptions = {
  isHidden?: boolean;
  moreRecommendationsPromise?: Promise<RecommendRoom[]> | null;
  onHideAction?: () => void;
  surface?: DesktopControlBarSurface;
  variant?: DesktopControlBarVariant;
};
const CONTROL_BAR_ACTION_CLASS = 'text-vive-primary-20 h-8 min-w-max shrink-0 rounded-full bg-white/15 px-4 py-2 backdrop-blur-[50px] hover:bg-white/20 hover:text-white active:bg-white/15 [&&_svg]:max-h-none @[954px]:h-[34px]';
export function DesktopControlBar(t0) {
  const $ = _c(69);
  const {
    moreRecommendationsPromise,
    onHideAction,
    popupContainer,
    surface: t1,
    variant: t2
  } = t0;
  const surface = t1 === undefined ? "normal" : t1;
  const variant = t2 === undefined ? "default" : t2;
  const t = useTranslations();
  const {
    handleLogin
  } = useAuthContext();
  const {
    roomData
  } = useCheckEnterRoomContext();
  const [loginAction, setLoginAction] = useState(null);
  const isProVariant = variant === "viverse-pro";
  const contentKind = roomData.content_type?.code === "games" ? "games" : "experiences";
  let t3;
  if ($[0] !== handleLogin) {
    t3 = () => {
      handleLogin({
        callback: window.location.href
      });
      setLoginAction(null);
    };
    $[0] = handleLogin;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const handleLoginAction = t3;
  let t4;
  if ($[2] !== isProVariant) {
    t4 = !isProVariant && <span className="relative h-4 w-[17px] shrink-0 @[801px]:h-[22px] @[801px]:w-6"><IconViverseLogo className="absolute top-0 left-0 h-[13px] w-4 @[801px]:h-[19.693px] @[801px]:w-[23.597px]" aria-hidden="true" /></span>;
    $[2] = isProVariant;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== roomData.title) {
    t5 = <span className="truncate text-sm leading-[22px] font-semibold @[801px]:text-base @[801px]:leading-6">{roomData.title}</span>;
    $[4] = roomData.title;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t4 || $[7] !== t5) {
    t6 = <div className="flex min-w-0 flex-1 items-center gap-2 @[705px]:w-[200px] @[705px]:flex-none @[801px]:w-[206px] @[971px]:w-[418.5px]">{t4}{t5}</div>;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== onHideAction || $[10] !== surface || $[11] !== t) {
    t7 = surface === "fullscreen" && <ViverseButton data-action="hide-control-bar" variant="custom" size="custom" className="bg-vive-primary-70 text-vive-primary-20 hover:bg-vive-primary-60 flex h-[34px] shrink-0 items-center gap-2 rounded-full px-4 text-sm leading-[22px] font-semibold backdrop-blur-[100px] hover:text-white" onClick={onHideAction}><IconHide className="size-5 shrink-0" aria-hidden="true" />{t("control-bar.hide-this-bar")}</ViverseButton>;
    $[9] = onHideAction;
    $[10] = surface;
    $[11] = t;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== contentKind || $[14] !== isProVariant || $[15] !== moreRecommendationsPromise || $[16] !== popupContainer) {
    t8 = !isProVariant && <ControlBarMoreAction contentKind={contentKind} moreRecommendationsPromise={moreRecommendationsPromise} popupContainer={popupContainer} />;
    $[13] = contentKind;
    $[14] = isProVariant;
    $[15] = moreRecommendationsPromise;
    $[16] = popupContainer;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const t9 = loginAction === "like";
  let t10;
  if ($[18] !== loginAction) {
    t10 = open => {
      if (!open && loginAction === "like") {
        setLoginAction(null);
      }
    };
    $[18] = loginAction;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => setLoginAction(_temp);
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== handleLoginAction || $[22] !== popupContainer || $[23] !== t10 || $[24] !== t9) {
    t12 = <ControlBarLikeAction className={CONTROL_BAR_ACTION_CLASS} isLoginPopoverOpen={t9} onLogin={handleLoginAction} onLoginPopoverOpenChange={t10} onSignedOutClick={t11} popupContainer={popupContainer} />;
    $[21] = handleLoginAction;
    $[22] = popupContainer;
    $[23] = t10;
    $[24] = t9;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t) {
    t13 = t("action-list.share");
    $[26] = t;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== popupContainer) {
    t14 = <SharePopoverButton className={CONTROL_BAR_ACTION_CLASS} iconClassName="size-4 @[954px]:size-5" portalContainer={popupContainer} size="custom" useControlBarDialog={true} />;
    $[28] = popupContainer;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== popupContainer || $[31] !== t13 || $[32] !== t14) {
    t15 = <ControlBarActionTooltip label={t13} portalContainer={popupContainer}>{t14}</ControlBarActionTooltip>;
    $[30] = popupContainer;
    $[31] = t13;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  const t16 = loginAction === "save";
  let t17;
  if ($[34] !== loginAction) {
    t17 = open_0 => {
      if (!open_0 && loginAction === "save") {
        setLoginAction(null);
      }
    };
    $[34] = loginAction;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => setLoginAction(_temp2);
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== handleLoginAction || $[38] !== popupContainer || $[39] !== t16 || $[40] !== t17) {
    t19 = <ControlBarSaveAction className={CONTROL_BAR_ACTION_CLASS} isLoginPopoverOpen={t16} onLogin={handleLoginAction} onLoginPopoverOpenChange={t17} onSignedOutClick={t18} popupContainer={popupContainer} />;
    $[37] = handleLoginAction;
    $[38] = popupContainer;
    $[39] = t16;
    $[40] = t17;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  const t20 = surface === "fullscreen" ? popupContainer : undefined;
  let t21;
  if ($[42] !== popupContainer || $[43] !== t20) {
    t21 = <ControlBarReportAction className={CONTROL_BAR_ACTION_CLASS} popupContainer={popupContainer} reportModalContainer={t20} />;
    $[42] = popupContainer;
    $[43] = t20;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  const t22 = surface === "fullscreen" ? "control-bar.exit-fullscreen" : "control-bar.fullscreen";
  let t23;
  if ($[45] !== t || $[46] !== t22) {
    t23 = t(t22);
    $[45] = t;
    $[46] = t22;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  const t24 = surface === "fullscreen" ? "control-bar.exit-fullscreen" : "control-bar.fullscreen";
  let t25;
  if ($[48] !== t || $[49] !== t24) {
    t25 = t(t24);
    $[48] = t;
    $[49] = t24;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== t25) {
    t26 = <span className="hidden text-sm leading-[22px] font-semibold @[954px]:inline">{t25}</span>;
    $[51] = t25;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] !== t23 || $[54] !== t26) {
    t27 = <FullscreenButton ariaLabel={t23} className={CONTROL_BAR_ACTION_CLASS} iconClassName="size-4 @[954px]:size-5" label={t26} />;
    $[53] = t23;
    $[54] = t26;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  let t28;
  if ($[56] !== t12 || $[57] !== t15 || $[58] !== t19 || $[59] !== t21 || $[60] !== t27) {
    t28 = <div className="ml-2 flex items-center gap-1 rounded-3xl backdrop-blur-xl">{t12}{t15}{t19}{t21}{t27}</div>;
    $[56] = t12;
    $[57] = t15;
    $[58] = t19;
    $[59] = t21;
    $[60] = t27;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== t28 || $[63] !== t8) {
    t29 = <div className="flex shrink-0 items-center justify-end">{t8}{t28}</div>;
    $[62] = t28;
    $[63] = t8;
    $[64] = t29;
  } else {
    t29 = $[64];
  }
  let t30;
  if ($[65] !== t29 || $[66] !== t6 || $[67] !== t7) {
    t30 = <div data-component="control-bar" className="bg-control-bar-surface @container relative flex min-h-12 w-full flex-nowrap items-center justify-between gap-2 rounded-b-lg p-2 text-white">{t6}{t7}{t29}</div>;
    $[65] = t29;
    $[66] = t6;
    $[67] = t7;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  return t30;
}
function _temp2(currentAction_0) {
  return currentAction_0 === "save" ? null : "save";
}
function _temp(currentAction) {
  return currentAction === "like" ? null : "like";
}
export function createDesktopControlBarViewportLayout({
  isHidden,
  moreRecommendationsPromise,
  onHideAction,
  surface = 'normal',
  variant = 'default'
}: ControlBarDesktopViewportOptions): Omit<ControlBarViewportLayoutProps, 'children'> {
  return {
    barPlacement: 'after',
    className: 'flex h-full min-h-0 min-w-0 flex-1 flex-col',
    dataComponent: 'control-bar-layout',
    dataControlBarHidden: isHidden,
    dataSurface: surface,
    renderControlBar: popupContainer => surface !== 'fullscreen' || !isHidden ? <DesktopControlBar moreRecommendationsPromise={moreRecommendationsPromise} onHideAction={onHideAction} popupContainer={popupContainer} surface={surface} variant={variant} /> : null,
    viewportDataComponent: 'control-bar-viewport'
  };
}